import { FaCheck } from "react-icons/fa"
import { Link } from "react-router-dom"

import { MultiSelectOption } from "components/inputs/MultiSelect"

import { formatFiatCurrency, UNAVAILABLE_DASH } from "util/formatter"

import { isMicroserviceTenant } from "config/featureFlags"

import { BONUS2_VIOLATIONS_URL } from "./api/requests"
import {
  fiatAmountNativeColumn,
  toBetbyColumns,
  toCasinoColumns,
  transactionAmountNativeColumn,
  userBalanceNativeColumn,
  winFiatAmountNativeColumn
} from "./data"
import { TableDataType } from "./eums"
import { TableColumnsInterface } from "./interfaces"

// A getter, not a module-level const: `isMicroserviceTenant()` is resolved from the backend after
// login, so the column set must be computed at render time.
export const getTransactionsColumns = (): TableColumnsInterface[] => [
  {
    value: "id",
    title: "Id",
    format: TableDataType.SHORTENED_STRING,
    sorting: true
  },
  {
    value: "user_id",
    title: "User_Id",
    format: TableDataType.SHORTENED_STRING,
    // link: (id: string) => `/user/${id}`,
    sorting: false
  },
  {
    value: "user",
    title: "User",
    format: TableDataType.STRING,
    sorting: false
  },
  // This page only ever offered the crypto reading of `amount`, so a microservice tenant — whose
  // rows are denominated in the player's own fiat coin — had no column showing the native figure
  // at all. Same single field either way: `amount` is already native, it was just rendered as
  // satoshis. Not sortable: raw minor units across mixed coins don't compare.
  isMicroserviceTenant()
    ? transactionAmountNativeColumn("Amount", false)
    : {
        value: "amount",
        title: "Crypto Amount",
        format: TableDataType.CRYPTO_CURRENCY,
        sorting: false
      },
  {
    value: "usd_amount",
    title: "USD Amount",
    format: TableDataType.FIAT_CURRENCY,
    sorting: true
  },
  {
    value: "status",
    title: "Transaction Status",
    format: TableDataType.STRING,
    sorting: true
  },
  {
    value: "coin",
    title: "Crypto Coin",
    format: TableDataType.STRING,
    sorting: true
  },
  {
    value: "method",
    title: "Method",
    format: TableDataType.STRING,
    sorting: false
  },
  {
    value: "processor",
    title: "Processor",
    format: TableDataType.STRING,
    sorting: false
  },
  {
    value: "terminalName",
    title: "Terminal",
    format: TableDataType.STRING,
    sorting: false
  },
  {
    value: "type",
    title: "Type",
    format: TableDataType.STRING,
    sorting: true,
    customRender: (type: string, row?: any) => {
      if (type === "TIP" && row?.payout_id) {
        const txId = row.payout_id as string
        if (txId.endsWith("_credit")) {
          return "TIP (received)"
        }
        if (txId.startsWith("tip_")) {
          return "TIP (sent)"
        }
      }
      return type
    }
  },
  {
    value: "other",
    title: "Tip counterparty",
    format: TableDataType.STRING,
    sorting: false,
    customRender: (_other: any, row?: any) => {
      if (row?.type !== "TIP") return "–"

      const payoutId = row?.payout_id as string | undefined
      const other = row?.other ?? {}

      const baseTxId = typeof payoutId === "string" ? payoutId.replace(/_credit$/, "") : ""
      const match = /^tip_([^_]+)_([^_]+)_/.exec(baseTxId)
      const senderIdFromTx = match?.[1]
      const recipientIdFromTx = match?.[2]

      const shortenId = (id: string, head = 6, tail = 4) => {
        if (!id) return ""
        if (id.length <= head + tail + 3) return id
        return `${id.slice(0, head)}...${id.slice(-tail)}`
      }

      let direction: "sent" | "received" | null = null
      if (other?.direction === "sent" || other?.direction === "received") {
        direction = other.direction
      } else if (typeof payoutId === "string" && payoutId.endsWith("_credit")) {
        direction = "received"
      } else if (typeof payoutId === "string" && payoutId.startsWith("tip_")) {
        direction = "sent"
      }

      let counterpartyId: string | null = null
      let counterpartyUsername: string | null = null

      if (direction === "received") {
        counterpartyId = other?.senderId ?? senderIdFromTx ?? null
        counterpartyUsername = other?.senderUsername ?? null
      } else if (direction === "sent") {
        counterpartyId = other?.recipientId ?? recipientIdFromTx ?? null
        counterpartyUsername = other?.recipientUsername ?? null
      } else {
        return "–"
      }

      if (!counterpartyId) return "–"

      const label = counterpartyUsername || shortenId(String(counterpartyId))

      return (
        <Link
          target="_blank"
          to={`/user/${counterpartyId}`}
          className="text-blue-500 hover:underline inline-block max-w-[180px] truncate align-middle"
          title={counterpartyUsername || String(counterpartyId)}
          onClick={(event) => event.stopPropagation()}
        >
          {label}
        </Link>
      )
    }
  },
  {
    value: "payout_id",
    title: "Payout Id",
    format: TableDataType.SHORTENED_STRING,
    sorting: false
  },
  {
    value: "cardNumber",
    title: "Card Number",
    format: TableDataType.STRING,
    sorting: false
  },
  {
    value: "created_at",
    title: "Created At",
    format: TableDataType.DATE,
    sorting: true
  },
  {
    value: "updated_at",
    title: "Updated At",
    format: TableDataType.DATE,
    sorting: true
  }
]

const bonusViolationColumn = (canOpenViolations: boolean): TableColumnsInterface => ({
  value: "bonus_violation",
  title: "Bonus Violation",
  format: TableDataType.STRING,
  sorting: false,
  customRender: (value: boolean | null | undefined) => {
    if (value === null || value === undefined) return UNAVAILABLE_DASH
    if (!value) return "No"

    const label = (
      <>
        <FaCheck size={12} />
        Yes
      </>
    )

    if (!canOpenViolations) return <span className="inline-flex items-center gap-1">{label}</span>

    return (
      <Link
        target="_blank"
        to={BONUS2_VIOLATIONS_URL}
        className="inline-flex items-center gap-1 text-blue-500 hover:underline"
        title="Open the Bonus Violations tab"
        onClick={(event) => event.stopPropagation()}
      >
        {label}
      </Link>
    )
  }
})

// A getter, not a module-level const: `isMicroserviceTenant()` is resolved from the backend after
// login, so the column set must be computed at render time.
export const getBankTransfersColumns = ({
  isSubmittedTab = false,
  canOpenViolations = false
}: { isSubmittedTab?: boolean; canOpenViolations?: boolean } = {}): TableColumnsInterface[] => [
  // {
  //   value: "id",
  //   title: "Id",
  //   format: TableDataType.STRING,
  //   sorting: true
  // },
  {
    value: "payout_id",
    title: "Transaction ID",
    format: TableDataType.STRING,
    sorting: false
  },
  {
    value: "user",
    title: "Username",
    format: TableDataType.STRING,
    sorting: false
  },
  // Microservice tenants carry a native `amount` (the transaction's own coin) beside the USD figure;
  // monolith-only tenants never do, so they keep the single pre-FS USD column.
  ...(isMicroserviceTenant() ? [transactionAmountNativeColumn("Amount", false)] : []),
  {
    value: "other.paymentMethod",
    title: "Method",
    format: TableDataType.STRING,
    sorting: false
  },
  // {
  //   value: "other.receiverName",
  //   title: "Receiver Name",
  //   format: TableDataType.STRING,
  //   sorting: false
  // },
  // {
  //   value: "other.iban",
  //   title: "IBAN",
  //   format: TableDataType.STRING,
  //   sorting: false
  // },
  // {
  //   value: "other.bic",
  //   title: "BIC",
  //   format: TableDataType.STRING,
  //   sorting: false
  // },
  // {
  //   value: "other.bankName",
  //   title: "Bank Name",
  //   format: TableDataType.STRING,
  //   sorting: false
  // },
  // {
  //   value: "other.bankAddress",
  //   title: "Bank Address",
  //   format: TableDataType.STRING,
  //   sorting: false
  // },
  // The USD equivalent, beside the native Amount rather than instead of it: withdrawal limits, the
  // KYC threshold and any comparison across rows are all in USD, and two crypto figures in different
  // coins cannot be compared by eye. Sits between Method and Balance so the money columns read
  // native -> USD -> balance.
  {
    value: "usd_amount",
    title: "USD Amount",
    format: TableDataType.FIAT_CURRENCY,
    sorting: true
  },
  // total_balance_coin / balance_unavailable are FS-only signals; monolith-only tenants don't emit
  // them, so they keep the pre-FS plain-USD Balance column.
  isMicroserviceTenant()
    ? {
        value: "total_balance",
        title: "Balance",
        // For microservice-owned tenants this is FS-authoritative (cash + bonus) in the player's
        // REAL wallet coin (total_balance_coin, e.g. PLN/CAD) — not forced through USD — so render
        // against that coin (same reasoning as nativeMoneyColumn/data.tsx) instead of hardcoding "$".
        // The backend fails closed with balance_unavailable:true (total_balance null) when FS/
        // bonus-service is down rather than serving the stale monolith wallet shadow — show "—".
        format: TableDataType.STRING,
        sorting: false,
        customRender: (value: number | null, row?: any) =>
          row?.balance_unavailable ? UNAVAILABLE_DASH : formatFiatCurrency(value ?? undefined, "$0.00", row?.total_balance_coin ?? "USD")
      }
    : {
        value: "total_balance",
        title: "Balance",
        format: TableDataType.FIAT_CURRENCY,
        sorting: false
      },
  {
    value: "kyc_status",
    title: "KYC Status",
    format: TableDataType.STRING,
    sorting: false
  },
  isSubmittedTab
    ? bonusViolationColumn(canOpenViolations)
    : {
        value: "other.reason",
        title: "Decline Reason",
        format: TableDataType.STRING,
        sorting: false
      },
  {
    value: "created_at",
    title: "Created At",
    format: TableDataType.DATE,
    sorting: true
  },
  {
    value: "updated_at",
    title: "Updated At",
    format: TableDataType.DATE,
    sorting: true
  }
]

export const betTypeOptions: MultiSelectOption[] = [
  { value: "CASINO", label: "CASINO" },
  { value: "SPORTS", label: "SPORTS" }
]

// Admin "All Bets" page (Bets / BetBy tabs on the Transactions page) — cross-user, so unlike
// the per-user tableUserBets (constants/data.tsx) this includes user_id/username, mirroring
// how getTransactionsColumns above adds them for the cross-user Transactions tab.
// `sorting` reflects the BetBy path; toCasinoColumns narrows it for the casino tab.
const adminBetsColumns: TableColumnsInterface[] = [
  {
    value: "id",
    title: "Id",
    format: TableDataType.SHORTENED_STRING,
    sorting: true
  },
  {
    value: "user_id",
    title: "User_Id",
    format: TableDataType.SHORTENED_STRING,
    sorting: false
  },
  {
    value: "username",
    title: "User",
    format: TableDataType.STRING,
    sorting: false
  },
  {
    value: "provider_name",
    title: "Provider",
    format: TableDataType.STRING,
    sorting: false,
    mntSorting: true
  },
  {
    value: "type",
    title: "Bet Type",
    format: TableDataType.STRING,
    sorting: false,
    mntSorting: true
  },
  {
    value: "sportsbet_status",
    title: "Sportsbet status",
    format: TableDataType.STRING,
    sorting: true
  },
  {
    value: "game_name",
    title: "Game Name",
    format: TableDataType.STRING,
    sorting: true
  },
  fiatAmountNativeColumn,
  {
    value: "crypto_amount",
    title: "Crypto Amount",
    format: TableDataType.CRYPTO_CURRENCY,
    sorting: true
  },
  winFiatAmountNativeColumn,
  {
    value: "win_crypto_amount",
    title: "Crypto Amount Won",
    format: TableDataType.CRYPTO_CURRENCY,
    sorting: true
  },
  {
    value: "coin",
    title: "Coin",
    format: TableDataType.STRING,
    sorting: true
  },
  userBalanceNativeColumn,
  {
    value: "finished",
    title: "Finished",
    format: TableDataType.BOOLEAN,
    sorting: true
  },
  {
    value: "rollbacked",
    title: "Rollbacked",
    format: TableDataType.BOOLEAN,
    sorting: true
  },
  {
    value: "created_at",
    title: "Created At",
    format: TableDataType.DATE,
    sorting: true
  }
]

export const tableAdminBetsColumns: TableColumnsInterface[] = toCasinoColumns(adminBetsColumns)

/**
 * BetBy bets tab — identical to the casino columns except every money column reverts to USD.
 * BetBy is monolith-owned (migrates last), so its rows are USD cents, not native.
 */
export const tableAdminBetbyBetsColumns: TableColumnsInterface[] = toBetbyColumns(adminBetsColumns)

/**
 * Casino "Bets" tab column set for the current tenant. Native (NUX) amounts only exist for
 * microservice tenants; monolith-only tenants never emit them, so they keep the pre-native USD
 * columns — which are exactly the reverted-to-USD BetBy set.
 */
export const getAdminBetsColumns = (): TableColumnsInterface[] =>
  isMicroserviceTenant() ? tableAdminBetsColumns : tableAdminBetbyBetsColumns

export enum EnumTransactionsPageTab {
  Transactions = "Transactions",
  Bets = "Bets",
  BetbyBets = "BetbyBets"
}
